/* Admin: pływające grafiki (drag & drop w mini-podglądzie).
   Pozwala wgrać obrazki do biblioteki i umieścić je na dowolnej podstronie
   w dowolnym punkcie (X, Y), ze skalowaniem, rotacją i widocznością. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Eye, EyeOff, ExternalLink, GripVertical } from "lucide-react";
import { api, adminAuthHeaders } from "@/lib/api";
import { inputCls, labelCls, PLACEMENT_PAGES } from "./constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

// Wymiary "wirtualnej" strony — placementy zapisywane są w px (top) i % (left)
// Preview używa iframe z rzeczywistą treścią podstrony, skalowany do PREVIEW_WIDTH
const PAGE_HEIGHT_PX = 2000;
const VIRTUAL_WIDTH = 1200;   // szerokość viewport dla iframe (tak strona się renderuje)
const PREVIEW_WIDTH = 480;    // szerokość mini-podglądu w pikselach
const PREVIEW_SCALE = PREVIEW_WIDTH / VIRTUAL_WIDTH; // 0.4

export default function PlacementsSection({ imagePlacements, setImagePlacements }) {
  const [selectedPage, setSelectedPage] = useState("home");
  const [library, setLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const previewRef = useRef(null);
  const dragState = useRef(null); // { id, offsetX, offsetY }

  const currentPage = PLACEMENT_PAGES.find((p) => p.id === selectedPage);
  const placementsForPage = imagePlacements.filter((p) => p.page === selectedPage);

  const loadLibrary = async () => {
    try {
      const { data } = await api.get("/admin/media", { headers: adminAuthHeaders() });
      setLibrary(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Nie udało się wczytać biblioteki grafik");
    }
  };

  useEffect(() => {
    loadLibrary();
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Plik musi być obrazem");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media", fd, {
        headers: { ...adminAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Grafika wgrana do biblioteki");
      await loadLibrary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd wgrywania");
    } finally {
      setUploading(false);
    }
  };

  const deleteFromLibrary = async (filename) => {
    if (!window.confirm(`Usunąć grafikę "${filename}" z biblioteki? To działanie jest nieodwracalne.`)) return;
    try {
      await api.delete(`/admin/media/${encodeURIComponent(filename)}`, { headers: adminAuthHeaders() });
      // Usuwamy też placementy używające tej grafiki
      setImagePlacements((prev) => prev.filter((p) => !p.url.endsWith(`/${filename}`)));
      await loadLibrary();
      toast.success("Grafika usunięta");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd usuwania");
    }
  };

  const addPlacementFromAsset = (asset) => {
    const id = `pl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newP = {
      id,
      page: selectedPage,
      url: asset.url,
      alt: "",
      top_px: 100,
      left_pct: 50,
      width_px: 200,
      rotation: 0,
      z_index: 20,
      visible: true,
    };
    setImagePlacements((prev) => [...prev, newP]);
    setSelectedId(id);
    toast.success("Dodano grafikę — przeciągnij ją w podglądzie");
  };

  const updatePlacement = (id, changes) => {
    setImagePlacements((prev) => prev.map((p) => (p.id === id ? { ...p, ...changes } : p)));
  };

  const removePlacement = (id) => {
    setImagePlacements((prev) => prev.filter((p) => p.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // === Drag & drop ===
  const onMouseDown = (e, placement) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(placement.id);
    const container = previewRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const scaleY = PAGE_HEIGHT_PX / rect.height; // px stronicowe / px podglądu
    // Zapamiętaj offset kursora od środka obrazka (aby drag nie skakał)
    const centerX = rect.left + (placement.left_pct / 100) * rect.width;
    const topPreview = rect.top + placement.top_px / scaleY;
    dragState.current = {
      id: placement.id,
      offsetX: e.clientX - centerX,
      offsetY: e.clientY - topPreview,
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp, { once: true });
  };

  const onMouseMove = (e) => {
    const state = dragState.current;
    if (!state) return;
    const container = previewRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const scaleY = PAGE_HEIGHT_PX / rect.height;
    const px = e.clientX - state.offsetX - rect.left; // środek grafiki w px podglądu
    const py = e.clientY - state.offsetY - rect.top;  // top grafiki w px podglądu
    const leftPct = Math.max(0, Math.min(100, (px / rect.width) * 100));
    const topPx = Math.max(0, Math.min(PAGE_HEIGHT_PX, py * scaleY));
    updatePlacement(state.id, { left_pct: Math.round(leftPct * 10) / 10, top_px: Math.round(topPx) });
  };

  const onMouseUp = () => {
    dragState.current = null;
    window.removeEventListener("mousemove", onMouseMove);
  };

  useEffect(() => () => window.removeEventListener("mousemove", onMouseMove), []);

  return (
    <div data-testid="section-placements" className="space-y-6">
      <div className="rounded-xl border border-slate-200 p-5 bg-white">
        <h3 className="font-bold text-lg text-[#1f3a8a] mb-2">Pływające grafiki (drag & drop)</h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          Wgrywaj obrazki do biblioteki, a następnie <b>przeciągaj</b> je myszką w mini-podglądzie,
          aby ustawić dokładną pozycję na wybranej podstronie. Grafiki nakładają się na tekst,
          ale nie blokują klikania (są „przezroczyste dla myszki” w publicznej wersji strony).
        </p>
      </div>

      {/* ================ BIBLIOTEKA MEDIÓW ================ */}
      <div className="rounded-xl border border-slate-200 p-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-bold text-slate-800">Biblioteka grafik</h4>
            <p className="text-xs text-slate-500 mt-1">Wgraj obrazki, aby móc je potem umieszczać na stronach.</p>
          </div>
          <label
            data-testid="placement-upload"
            className="inline-flex items-center gap-2 rm-btn-primary cursor-pointer !py-2 !px-4 text-sm"
          >
            <Upload size={16} />
            {uploading ? "Wgrywanie..." : "Wgraj grafikę"}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              disabled={uploading}
            />
          </label>
        </div>

        {library.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">
            Brak grafik w bibliotece. Wgraj pierwszy obrazek.
          </div>
        ) : (
          <div data-testid="placement-library" className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {library.map((asset) => (
              <div key={asset.filename} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                <img
                  src={fullUrl(asset.url)}
                  alt={asset.filename}
                  className="w-full h-24 object-contain bg-slate-100"
                  loading="lazy"
                />
                <div className="p-1.5 text-[10px] text-slate-500 truncate" title={asset.filename}>
                  {asset.filename}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-1.5 bg-black/60 opacity-0 group-hover:opacity-100 transition flex gap-1">
                  <button
                    data-testid={`placement-add-${asset.filename}`}
                    type="button"
                    onClick={() => addPlacementFromAsset(asset)}
                    className="flex-1 text-[10px] font-semibold text-white bg-[#dc2626] hover:bg-[#ef4444] px-1.5 py-1 rounded"
                  >
                    <Plus size={11} className="inline" /> Dodaj
                  </button>
                  <button
                    data-testid={`placement-del-${asset.filename}`}
                    type="button"
                    onClick={() => deleteFromLibrary(asset.filename)}
                    className="text-[10px] text-white/80 hover:text-white bg-slate-700/60 hover:bg-red-500 px-1.5 py-1 rounded"
                    title="Usuń z biblioteki"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================ PODGLĄD I POZYCJE ================ */}
      <div className="rounded-xl border border-slate-200 p-5 bg-white">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h4 className="font-bold text-slate-800">Podgląd — pozycje na stronie</h4>
            <p className="text-xs text-slate-500 mt-1">
              Przeciągnij grafikę w podglądzie, aby zmienić jej pozycję. Kliknij grafikę aby ją zaznaczyć i edytować.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="placement-page-select"
              value={selectedPage}
              onChange={(e) => { setSelectedPage(e.target.value); setSelectedId(null); }}
              className={inputCls}
              style={{ width: "auto" }}
            >
              {PLACEMENT_PAGES.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            {currentPage && (
              <a
                data-testid="placement-preview-link"
                href={currentPage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#1f3a8a] hover:text-[#dc2626] px-3 py-2 rounded-lg border border-slate-200 hover:border-[#dc2626] transition"
              >
                <ExternalLink size={13} /> Otwórz podstronę
              </a>
            )}
          </div>
        </div>

        {/* Mini podgląd — rzeczywisty widok strony w iframe + grafiki na wierzchu */}
        <div
          className="relative w-full mx-auto rounded-xl overflow-hidden border-2 border-slate-300 shadow-inner select-none bg-slate-900"
          style={{
            width: `${PREVIEW_WIDTH}px`,
            height: `${PAGE_HEIGHT_PX * PREVIEW_SCALE}px`,
          }}
        >
          {/* Iframe z prawdziwą treścią podstrony — skalowany "in place" */}
          <iframe
            key={selectedPage}
            title="preview"
            src={currentPage?.url || "/"}
            data-testid="placement-preview-iframe"
            scrolling="no"
            style={{
              width: `${VIRTUAL_WIDTH}px`,
              height: `${PAGE_HEIGHT_PX}px`,
              border: 0,
              transform: `scale(${PREVIEW_SCALE})`,
              transformOrigin: "top left",
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />

          {/* Warstwa placementów — przechwytuje drag&drop */}
          <div
            ref={previewRef}
            data-testid="placement-preview"
            className="absolute inset-0"
          >
            <div className="absolute top-2 left-2 text-[10px] font-bold text-white/70 uppercase tracking-wider pointer-events-none bg-black/40 px-1.5 py-0.5 rounded z-[9999]">
              {currentPage?.label} — podgląd
            </div>

            {placementsForPage.length === 0 && (
              <div className="absolute inset-x-0 top-14 text-center text-white/80 text-xs px-6 pointer-events-none z-[9999]">
                <span className="inline-block bg-black/60 px-3 py-1.5 rounded">
                  Kliknij „Dodaj" pod grafiką w bibliotece, aby umieścić ją tutaj.
                </span>
              </div>
            )}

            {placementsForPage.map((p) => {
              const isSelected = selectedId === p.id;
              // Skala 1:1 z rzeczywistą stroną — VIRTUAL_WIDTH-based
              return (
                <div
                  key={p.id}
                  data-testid={`placement-item-${p.id}`}
                  onMouseDown={(e) => onMouseDown(e, p)}
                  onClick={() => setSelectedId(p.id)}
                  style={{
                    position: "absolute",
                    left: `${p.left_pct}%`,
                    top: `${(p.top_px / PAGE_HEIGHT_PX) * 100}%`,
                    transform: `translateX(-50%) rotate(${p.rotation}deg)`,
                    width: `${(p.width_px / VIRTUAL_WIDTH) * 100}%`,
                    zIndex: p.z_index,
                    opacity: p.visible ? 1 : 0.35,
                    cursor: "grab",
                    outline: isSelected ? "2px dashed #ef4444" : "1px dashed rgba(255,255,255,0.5)",
                    outlineOffset: 1,
                  }}
                >
                  <img
                    src={fullUrl(p.url)}
                    alt={p.alt || ""}
                    draggable={false}
                    className="w-full h-auto block pointer-events-none"
                  />
                  {isSelected && (
                    <div className="absolute -top-4 -left-1 bg-[#ef4444] text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow whitespace-nowrap pointer-events-none">
                      <GripVertical size={10} className="inline -ml-0.5" /> {Math.round(p.left_pct)}% / {p.top_px}px
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-[11px] text-slate-500 mt-2 text-center italic">
          Podgląd pokazuje rzeczywistą treść podstrony. Twoje grafiki nakładają się na wierzchu — przeciągnij je myszką.
        </p>
      </div>

      {/* ================ LISTA I EDYCJA POJEDYNCZYCH GRAFIK ================ */}
      <div className="rounded-xl border border-slate-200 p-5 bg-white">
        <h4 className="font-bold text-slate-800 mb-3">
          Grafiki na tej podstronie ({placementsForPage.length})
        </h4>
        {placementsForPage.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center italic">
            Brak grafik na tej podstronie.
          </p>
        ) : (
          <div className="space-y-3">
            {placementsForPage.map((p, idx) => (
              <div
                key={p.id}
                data-testid={`placement-row-${p.id}`}
                onClick={() => setSelectedId(p.id)}
                className={`rounded-lg border p-3 cursor-pointer transition ${
                  selectedId === p.id
                    ? "border-[#dc2626] bg-red-50/50 shadow-sm"
                    : "border-slate-200 bg-slate-50 hover:border-slate-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <img
                    src={fullUrl(p.url)}
                    alt=""
                    className="w-16 h-16 object-contain bg-white rounded border border-slate-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-6 gap-2 items-start">
                    <div>
                      <label className={labelCls}>Poz. X (%)</label>
                      <input
                        data-testid={`placement-left-${p.id}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={p.left_pct}
                        onChange={(e) => updatePlacement(p.id, { left_pct: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Poz. Y (px)</label>
                      <input
                        data-testid={`placement-top-${p.id}`}
                        type="number"
                        min="0"
                        max="10000"
                        step="10"
                        value={p.top_px}
                        onChange={(e) => updatePlacement(p.id, { top_px: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Szerokość (px)</label>
                      <input
                        data-testid={`placement-width-${p.id}`}
                        type="number"
                        min="20"
                        max="2000"
                        step="10"
                        value={p.width_px}
                        onChange={(e) => updatePlacement(p.id, { width_px: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Obrót (°)</label>
                      <input
                        data-testid={`placement-rotation-${p.id}`}
                        type="number"
                        min="-360"
                        max="360"
                        step="5"
                        value={p.rotation}
                        onChange={(e) => updatePlacement(p.id, { rotation: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Warstwa (z)</label>
                      <input
                        data-testid={`placement-zindex-${p.id}`}
                        type="number"
                        min="0"
                        max="9999"
                        step="1"
                        value={p.z_index}
                        onChange={(e) => updatePlacement(p.id, { z_index: Number(e.target.value) })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Alt (opis)</label>
                      <input
                        data-testid={`placement-alt-${p.id}`}
                        type="text"
                        value={p.alt}
                        onChange={(e) => updatePlacement(p.id, { alt: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className={inputCls}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-6 flex items-center gap-2 mt-1 pt-2 border-t border-slate-200">
                      <button
                        data-testid={`placement-visible-${p.id}`}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); updatePlacement(p.id, { visible: !p.visible }); }}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition ${
                          p.visible
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "border-slate-300 bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                      >
                        {p.visible ? <><Eye size={13} /> Widoczna</> : <><EyeOff size={13} /> Ukryta</>}
                      </button>
                      <span className="text-xs text-slate-400">#{idx + 1}</span>
                      <span className="text-xs text-slate-400 truncate flex-1">{p.url}</span>
                      <button
                        data-testid={`placement-delete-${p.id}`}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removePlacement(p.id); }}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-white hover:bg-red-500 px-2.5 py-1.5 rounded-md border border-red-200 hover:border-red-500 transition"
                      >
                        <Trash2 size={13} /> Usuń
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-4 italic">
          Pamiętaj: <b>Zapisz zmiany</b> na dole strony, aby utrwalić pozycje w bazie.
        </p>
      </div>
    </div>
  );
}
